import { EditMode } from './model/edit-mode';
import { showNode, hideNode, degreesToRadians, radiansToDegrees } from './helpers';
import { Multiselect } from './model/multiselect';
import { InstallationType } from './model/planarea';
import NumberFormatter from '../utils/number-formatter';
import { cm2ToMm2, cmToMm } from '../utils/number-converter';
import { currencySymbolBefore } from '../utils/price-formatter';

export class UI {
  menus = [];

  constructor(options) {
    this.toolbar = document.querySelector(options.toolbarSelector);
    this.canvas = document.querySelector(options.canvasSelector);
    this.sidebar = document.querySelector(options.sidebarSelector);
    this.configurator = document.querySelector(options.configuratorSelector);
    this.stats = document.querySelector(options.statsSelector);
    this.onZoomIn = options.onZoomIn;
    this.onZoomOut = options.onZoomOut;
    this.onModeChanged = options.onModeChanged;
    this.onBorderMenuClosed = options.onBorderMenuClosed;
    this.onAreaMenuClosed = options.onAreaMenuClosed;
    this.onBaseBorderClicked = options.onBaseBorderChangeRequest;
    this.onBoardsLayoutChanged = options.onBoardsLayoutChanged;
    this.onBoardsLayoutChangedStep = options.onBoardsLayoutChangedStep;
    this.onSelectedBoardLengthsChanged = options.onSelectedBoardLengthsChanged;
    this.onInstallationTypeChanged = options.onInstallationTypeChanged;
    this.onSplitPerpendicular = options.onSplitPerpendicular;
    this.onSplitParallel = options.onSplitParallel;
    this.onSplitCanceled = options.onSplitCanceled;
    this.onBreakerBoardsPlaced = options.onBreakerBoardsPlaced;
    this.onAngleChanged = options.onAngleChanged;
    this.onJoistSpacingPerpendicularChanged = options.onJoistSpacingPerpendicularChanged;
    this.onJoistSpacingParallelChanged = options.onJoistSpacingParallelChanged;
    this.onBorderDelete = options.onBorderDelete;
    this.onTileShiftChanged = options.onTileShiftChanged;
    this.onTrimLabelChanged = options.onTrimLabelChanged;
    this.editMode = options.editMode;
    this.borderMenu = this.sidebar.querySelector('[data-tool="border-menu"]');
    this.planareaMenu = this.sidebar.querySelector('[data-tool="planarea-menu"]');
    this.splitMenu = this.sidebar.querySelector('[data-tool="split-menu"]');
    this.breakerBoardNone = this.borderMenu.querySelector('[data-tool="breaker-board-none"]');
    this.breakerBoard1PlankButton = this.borderMenu.querySelector('[data-tool="breaker-board-1-plank"]');
    this.breakerBoard2PlankButton = this.borderMenu.querySelector('[data-tool="breaker-board-2-plank"]');
    this.baseBorderLink = this.planareaMenu.querySelector('[data-tool="base-border"]');
    this.boardsLayoutSelect = this.planareaMenu.querySelector('[data-tool="boards-layout"]');
    this.joistSpacingPerpendicularSelect = this.planareaMenu.querySelector('[data-tool="joist-spacing-perpendicular"]');
    this.joistSpacingParallelSelect = this.planareaMenu.querySelector('[data-tool="joist-spacing-parallel"]');
    this.boardLengthsSelect = this.planareaMenu.querySelector('[data-tool="board-lengths"]');
    this.installationTypeButtjointButton = this.planareaMenu.querySelector('[data-tool="installation-type-buttjoint"]');
    this.installationTypeStaggeredButton = this.planareaMenu.querySelector('[data-tool="installation-type-staggered"]');
    this.deleteBorderButton = this.borderMenu.querySelector('[data-tool="delete-border"]');
    this.menus.push(this.borderMenu, this.planareaMenu);
    this.splitEnterButton = this.planareaMenu.querySelector('[data-tool="area-split-enter"]');
    this.splitParpendicularButton = this.splitMenu.querySelector('[data-tool="split-90"]');
    this.splitParallelButton = this.splitMenu.querySelector('[data-tool="split-parallel"]');
    this.anglePerpendicularButton = this.planareaMenu.querySelector('[data-tool="boards-angle-perpendicular"]');
    this.angleParallelButton = this.planareaMenu.querySelector('[data-tool="boards-angle-parallel"]');
    this.angleCustomButton = this.planareaMenu.querySelector('[data-tool="boards-angle-custom"]');
    this.angleCustomInput = this.planareaMenu.querySelector('[data-tool="boards-angle-custom-value"]');
    this.tilesTranslationInput = this.planareaMenu.querySelector('[data-tool="tiles-translation-value"]');
    this.tileShiftContainer = this.planareaMenu.querySelector('[data-tool="tile-shift"]');
    this.tileShiftSelect = null;
    this.usedLengthsListTotal = this.stats.querySelector('[data-tool="board-lengths-list-total"]');
    this.usedLengthsListArea = this.stats.querySelector('[data-tool="board-lengths-list-area"]');
    this.planStats = this.stats.querySelector('[data-tool="plan-stats"]');
    this.areaStats = this.stats.querySelector('[data-tool="area-stats"]');
    this.profiModeSwitch = this.sidebar.querySelector('[data-tool="profi-mode-switch"]');
    this.menus.push(this.borderMenu, this.planareaMenu, this.splitMenu);
    this.translator = options.translator;
    this.additionalOptions = options.additionalOptions;
    this.resetLinks = this.sidebar.querySelectorAll('[data-tool="reset-link"]');
    this.debug = options.debug ? options.debug : false;
    this.numberFormatter = new NumberFormatter({});
    this.formatPatternJoistSpacing = this.numberFormatter.isImperial() ? 'inch_fraction' : 'mm';
    this.formatPatternBoardLengths = this.numberFormatter.isImperial() ? 'feet_inch' : 'cm';
    this.formatPatternArea = this.numberFormatter.isImperial() ? 'ft2' : 'm2';
    this.priceStats = document.querySelector(options.priceStatsSelector);
    this.priceStat = this.priceStats.querySelector('[data-tool="surface-price"]');
    this.priceStatHeading = this.priceStats.querySelector('[data-tool="surface-price-heading"]');
    this.wasteStat = this.priceStats.querySelector('[data-tool="surface-waste"]');

    this._disableUIFeatures();

    this._initToolbar();

    this._initSidebar(options);
  }

  updateStats(editState, planarea) {
    const boardWidthWithoutGap = this.additionalOptions.boardWidthWithoutGap;
    this._updateUsedBoardsTotal(editState.usedBoardLengthCounts, boardWidthWithoutGap);
    this._updatePlanStats(editState, boardWidthWithoutGap);
    if (planarea) {
      this._updateUsedBoardsArea(planarea.getUsedBoardLengthCounts(), boardWidthWithoutGap);
      this._updateAreaStats(planarea);
    }
  }

  shouldShowMetricArea() {
    return this.numberFormatter.isImperial();
  }

  borderActivated(border) {
    this._openSidebar();
    this._openBorderMenu(border);
  }

  planareaActivated(planarea, singleAreaPlan = false) {
    this._updateBoardsLayoutSelect(planarea);
    this._updateJoistSpacingPerpendicularSelect(planarea);
    this._updateJoistSpacingParallelSelect(planarea);
    if (this.additionalOptions.boardLengthsSelection) {
      this._updateBoardLengthsSelectR(planarea);
    }
    this._updateInstallationTypeButtons(planarea);
    this._updateAreaAngle(planarea);
    if (this.additionalOptions.tiles) {
      this._updateTileShiftSelect(planarea);
    }
    this._openSidebar();
    this._openPlanareaMenu(planarea);

    if (singleAreaPlan) {
      this._hideUsedBoardsArea();
    } else {
      this._showUsedBoardsArea();
    }
    const boardWidthWithoutGap = this.additionalOptions.boardWidthWithoutGap;
    this._updateUsedBoardsArea(planarea.getUsedBoardLengthCounts(), boardWidthWithoutGap);
    this._updateAreaStats(planarea);
  }

  planareasDeactivated() {
    this._hideUsedBoardsArea();
  }

  _openSidebar() {
    this.configurator.classList.add('sidebar-opened');
  }

  _closeSidebar() {
    this.configurator.classList.remove('sidebar-opened');
  }

  _initToolbar() {
    this.zoomInButton = this.toolbar.querySelector('[data-tool="zoom-in"]');
    this.zoomInButton.addEventListener('click', () => {
      this.onZoomIn();
    });

    this.zoomInButton = this.toolbar.querySelector('[data-tool="zoom-out"]');
    this.zoomInButton.addEventListener('click', () => {
      this.onZoomOut();
    });

    // keyDown listener for + and - zooming
    document.addEventListener('keydown', e => {
      switch (e.which) {
        case 187: // + (also allow =)
          e.stopPropagation();
          e.preventDefault();
          this.onZoomIn();
          break;
        case 107: // + on NumPad
          e.stopPropagation();
          e.preventDefault();
          this.onZoomIn();
          break;
        case 189: // - (also allow _)
          e.stopPropagation();
          e.preventDefault();
          this.onZoomOut();
          break;
        case 109: // - on NumPad
          e.stopPropagation();
          e.preventDefault();
          this.onZoomOut();
          break;
      }
    });

    this.modeButton = this.toolbar.querySelector('[data-tool="mode"]');
    this.modeButton.addEventListener('click', e => {
      if (this.editMode === EditMode.POINTER) {
        this.editMode = EditMode.MOVE;
        this.modeButton.querySelector('span').className = 'mptp-icon icon-export_selection-icon';
        dojo.style(this.canvas, 'cursor', 'move');
      } else {
        this.editMode = EditMode.POINTER;
        this.modeButton.querySelector('span').className = 'fa fa-arrows';
        dojo.style(this.canvas, 'cursor', 'default');
      }
      this.onModeChanged(this.editMode);
    });
    this.resetLinks.forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault();
        let confirmed = confirm(this.translator.translate('planRecalculationWarning'));
        if (confirmed) {
          e.currentTarget.parentElement.submit();
        }
      });
    });
  }

  _initSidebar() {
    this._closeAllMenus();
    this.sidebar.querySelector('[data-tool="border-close"]').addEventListener('click', () => {
      this._openPlanareaMenu();
      this.onBorderMenuClosed();
    });

    if (this.sidebar.querySelector('[data-tool="split-close"]')) {
      this.sidebar.querySelector('[data-tool="split-close"]').addEventListener('click', () => {
        this._closeAllMenus();
        this._closeSidebar();
        this.onAreaMenuClosed();
      });
    }

    this.sidebar.querySelector('[data-tool="split-back"]').addEventListener('click', () => {
      this._closeAllMenus();
      this._deactivateSplitButtons();
      this._openPlanareaMenu();
      this.onSplitCanceled();
    });

    this.baseBorderLink.addEventListener('click', e => {
      e.preventDefault();
      this.onBaseBorderClicked();
    });

    this.boardsLayoutSelect.addEventListener('change', e => {
      let selectedIndex = parseInt(this.boardsLayoutSelect.value);
      this.onBoardsLayoutChanged(selectedIndex);
    });

    if (this.joistSpacingPerpendicularSelect) {
      this.joistSpacingPerpendicularSelect.addEventListener('change', e => {
        let selectedIndex = parseFloat(this.joistSpacingPerpendicularSelect.value);
        this.onJoistSpacingPerpendicularChanged(selectedIndex);
      });
    }

    if (this.joistSpacingParallelSelect) {
      this.joistSpacingParallelSelect.addEventListener('change', e => {
        let selectedIndex = parseFloat(this.joistSpacingParallelSelect.value);
        this.onJoistSpacingParallelChanged(selectedIndex);
      });
    }

    if (this.additionalOptions.boardLengthsSelection) {
      this._initBoardLengthsSelectR();
    }

    if (this.installationTypeButtjointButton) {
      this.installationTypeButtjointButton.addEventListener('click', () => {
        this.onInstallationTypeChanged(InstallationType.buttjoint);
      });
    }

    if (this.installationTypeStaggeredButton) {
      this.installationTypeStaggeredButton.addEventListener('click', () => {
        this.onInstallationTypeChanged(InstallationType.staggered);
      });
    }

    if (this.additionalOptions.splitting) {
      this.splitEnterButton.addEventListener('click', () => {
        this._openSplitMenu();
      });

      this.splitParpendicularButton.addEventListener('click', () => {
        this._deactivateSplitButtons();
        this._activateButton(this.splitParpendicularButton);
        this.onSplitPerpendicular();
      });

      this.splitParallelButton.addEventListener('click', () => {
        this._deactivateSplitButtons();
        this._activateButton(this.splitParallelButton);
        this.onSplitParallel();
      });
    }

    this._handleProfiModeChange(this.profiModeSwitch.checked);

    this.profiModeSwitch.addEventListener('change', e => {
      this._handleProfiModeChange(e.target.checked);
    });

    this.breakerBoard1PlankButton.addEventListener('click', () => {
      this.onBreakerBoardsPlaced(1);
    });

    this.breakerBoard2PlankButton.addEventListener('click', () => {
      this.onBreakerBoardsPlaced(2);
    });

    this.breakerBoardNone.addEventListener('click', () => {
      this.onBreakerBoardsPlaced(0);
    });

    this.angleParallelButton.addEventListener('click', () => {
      hideNode(this.angleCustomInput);
      this._clearCustomAnlge();
      this._deactivateAngleButtons();
      this._activateButton(this.angleParallelButton);
      this.onAngleChanged(0);
    });

    this.anglePerpendicularButton.addEventListener('click', () => {
      hideNode(this.angleCustomInput);
      this._clearCustomAnlge();
      this._deactivateAngleButtons();
      this._activateButton(this.anglePerpendicularButton);
      this.onAngleChanged(degreesToRadians(90));
    });

    if (this.additionalOptions.freeDirection) {
      this.angleCustomButton.addEventListener('click', () => {
        showNode(this.angleCustomInput, 'inline-block');
        this.angleCustomInput.focus();
        this._deactivateAngleButtons();
        this._activateButton(this.angleCustomButton);
      });

      this.angleCustomInput.addEventListener('change', e => {
        this._handleCustomAngleChange(e.target.value);
      });
    }

    if (this.additionalOptions.tiles && this.tileShiftContainer) {
      const shouldDisableSelect = this.additionalOptions.tileShift.length == 1;
      const smartSelect = this._buildSmartSelect(shouldDisableSelect);
      this.tileShiftSelect = smartSelect.querySelector('select');
      this.tileShiftContainer.appendChild(smartSelect);

      this.tileShiftSelect.addEventListener('change', e => {
        let selectedIndex = parseInt(this.tileShiftSelect.value);
        this.onTileShiftChanged(selectedIndex);
      });
    }

    this.deleteBorderButton.addEventListener('click', () => {
      this.onBorderDelete();
    });
  }

  _openBorderMenu(border) {
    this._closeAllMenus();
    this._updateDeleteBorder(border);
    this._updateBreakerBoardButtons(border);
    this._renderBorderEdgeLabels(border, this.sidebar.querySelector('[data-tool="border-edge-labels"]'));

    showNode(this.borderMenu);
  }

  _updateDeleteBorder(border) {
    if (border.canBeDeleted()) {
      showNode(this.deleteBorderButton);
    } else {
      hideNode(this.deleteBorderButton);
    }
  }

  _updateBreakerBoardButtons(border) {
    this._deactivateBreakerBoardButtons();
    const plankCount = border.getBreakerBoardPlankCount();
    switch (plankCount) {
      case 0:
        this._activateButton(this.breakerBoardNone);
        break;
      case 1:
        this._activateButton(this.breakerBoard1PlankButton);
        break;
      case 2:
        this._activateButton(this.breakerBoard2PlankButton);
        break;
    }
  }

  _deactivateBreakerBoardButtons() {
    this._deactivateButton(this.breakerBoardNone);
    this._deactivateButton(this.breakerBoard1PlankButton);
    this._deactivateButton(this.breakerBoard2PlankButton);
  }

  _openPlanareaMenu() {
    this._closeAllMenus();
    showNode(this.planareaMenu);
  }

  _openSplitMenu() {
    this._deactivateSplitButtons();
    this._closeAllMenus();
    showNode(this.splitMenu);
    this._activateButton(this.splitParpendicularButton);
    this.onSplitPerpendicular();
  }

  _activateButton(button) {
    if (button) {
      button.classList.add('selected');
    }
  }

  _deactivateButton(button) {
    if (button) {
      button.classList.remove('selected');
    }
  }

  _deactivateSplitButtons() {
    this._deactivateButton(this.splitParallelButton);
    this._deactivateButton(this.splitParpendicularButton);
  }

  _deactivateAngleButtons() {
    this._deactivateButton(this.angleCustomButton);
    this._deactivateButton(this.angleParallelButton);
    this._deactivateButton(this.anglePerpendicularButton);
  }

  _deactivateInstallationTypeButtons() {
    this._deactivateButton(this.installationTypeStaggeredButton);
    this._deactivateButton(this.installationTypeButtjointButton);
  }

  _closeAllMenus() {
    this.menus.forEach(menu => {
      hideNode(menu);
    });
  }

  _updateBoardsLayoutSelect(planarea) {
    this._clearSelect(this.boardsLayoutSelect);
    const wrapper = this.boardsLayoutSelect.parentElement.parentElement.parentElement;
    if (planarea.isBoardLayoutButtjoint()) {
      showNode(wrapper);
      const sortedPlans = planarea.getIndexedBoardLayoutsSorted();
      const numberOfLayouts = sortedPlans.length;
      sortedPlans.forEach((indexedLayout, selectorIndex) => {
        const layout = indexedLayout.layout;
        const index = indexedLayout.index;
        const option = document.createElement('option');
        const isRatioStar =
          this.debug && layout.stats.optimization && layout.stats.optimization.id === 'boardsRatio'
            ? '★'
            : this.debug && layout.stats.optimization && layout.stats.optimization.id === 'lessWaste'
            ? '🤞'
            : '';
        const isDebug = this.debug && layout.stats && layout.stats.debug === 'to_be_deleted' ? '❌' : '';
        const isSymmetrical = this.debug && layout.stats && layout.stats.debug1 === 'symmetrical' ? '❤️' : '';
        const spacer = '\xa0';
        option.value = index;
        option.text = `${selectorIndex + 1}/${numberOfLayouts}: ${isRatioStar}${isDebug}${isSymmetrical}${spacer.repeat(
          3
        )}${this.translator.translate('waste')}: ${Math.round(layout.stats.norm.waste_linear * 100)} %`;
        if (index === planarea.areaState.currentPlanIndex) {
          option.selected = true;
        }
        this.boardsLayoutSelect.add(option);
      });
    } else {
      hideNode(wrapper);
    }
  }

  _updateJoistSpacingPerpendicularSelect(planarea) {
    // joistSpacingPerpendicularAlternatives are provided in mm, rendered as mm, but sent to algo in cm
    if (!this.joistSpacingPerpendicularSelect) return;
    this._clearSelect(this.joistSpacingPerpendicularSelect);
    this.additionalOptions.joistSpacingPerpendicularAlternatives.forEach(joistSpacing => {
      const option = document.createElement('option');
      option.value = joistSpacing / 10;
      option.text = this.numberFormatter.formatOutput(joistSpacing, this.formatPatternJoistSpacing, false);
      if (joistSpacing / 10 === planarea.areaState.currentJoistSpacingPerpendicular) {
        option.selected = true;
      }
      this.joistSpacingPerpendicularSelect.add(option);
    });
  }

  _updateJoistSpacingParallelSelect(planarea) {
    // joistSpacingPerpendicularAlternatives are provided in mm, rendered as mm, but sent to algo in cm
    if (!this.joistSpacingParallelSelect) return;
    this._clearSelect(this.joistSpacingParallelSelect);
    this.additionalOptions.joistSpacingParallelAlternatives.forEach(joistSpacing => {
      const option = document.createElement('option');
      option.value = joistSpacing / 10;
      option.text = this.numberFormatter.formatOutput(joistSpacing, this.formatPatternJoistSpacing, false);
      if (joistSpacing / 10 === planarea.areaState.currentJoistSpacingParallel) {
        option.selected = true;
      }
      this.joistSpacingParallelSelect.add(option);
    });
  }

  _updateTileShiftSelect(planarea) {
    if (!this.tileShiftSelect) return;
    this._clearSelect(this.tileShiftSelect);
    this.additionalOptions.tileShift.forEach(tileShift => {
      const option = document.createElement('option');
      option.value = tileShift;
      option.text = this._formatTileShift(tileShift);
      if (tileShift == planarea.areaState.currentTileShift) {
        option.selected = true;
      }
      this.tileShiftSelect.add(option);
    });
  }

  _clearSelect(select) {
    while (select.firstChild) {
      select.removeChild(select.firstChild);
    }
  }

  _initBoardLengthsSelectR() {
    const options = this.additionalOptions.availableBoards.map(board => {
      return {
        value: board.length,
        text: this.numberFormatter.formatOutput(cmToMm(board.length), this.formatPatternBoardLengths),
        disabled: board.priority != null && board.priority < 0,
      };
    });
    this.boardLengthsSelectR = new Multiselect({
      element: this.boardLengthsSelect,
      options: this.additionalOptions.showDisabledLengths ? options : options.filter(option => !option.disabled),
      onChange: this.onSelectedBoardLengthsChanged,
    });
  }

  _updateBoardLengthsSelectR(planarea) {
    this.boardLengthsSelectR.update(planarea.getSelectedBoardLengths());
  }

  _updateInstallationTypeButtons(planarea) {
    this._deactivateInstallationTypeButtons();
    switch (planarea.getInstallationType()) {
      case InstallationType.staggered:
        this._activateButton(this.installationTypeStaggeredButton);
        break;
      case InstallationType.buttjoint:
        this._activateButton(this.installationTypeButtjointButton);
        break;
    }
  }

  _updateAreaAngle(planarea) {
    const radians = parseFloat(planarea.areaState.rawArea.data.angle_rad);
    const degrees = radiansToDegrees(radians);
    this._deactivateAngleButtons();
    this._clearCustomAnlge();
    switch (degrees) {
      case 0:
        hideNode(this.angleCustomInput);
        this._activateButton(this.angleParallelButton);
        break;
      case 90:
        hideNode(this.angleCustomInput);
        this._activateButton(this.anglePerpendicularButton);
        break;
      default:
        showNode(this.angleCustomInput, 'inline-block');
        this._activateButton(this.angleCustomButton);
        this.angleCustomInput.value = Math.round(degrees);
        break;
    }
  }

  _updateTilesTranslation(planarea) {
    const value = planarea.areaState.tilesTranslation;
    this.tilesTranslationInput.value = Math.round(value * 100);
  }

  _handleCustomAngleChange(value) {
    const intVal = parseInt(value);
    if (intVal >= 0 && intVal < 180) {
      this.angleCustomInput.blur();
      this.onAngleChanged(degreesToRadians(intVal));
    } else {
      this.angleCustomInput.value = '';
    }
  }

  _clearCustomAnlge() {
    this.angleCustomInput.value = '';
  }

  _disableUIFeatures() {
    if (!this.additionalOptions.freeDirection) {
      this.angleCustomButton.parentElement.remove();
    }

    if (!this.additionalOptions.boardLengthsSelection) {
      this.boardLengthsSelect.parentElement.parentElement.remove();
      this.usedLengthsListTotal.parentElement.remove();
      this.usedLengthsListArea.parentElement.remove();
    }

    if (!this.additionalOptions.splitting) {
      this.splitEnterButton.parentElement.remove();
    }

    if (!this.additionalOptions.breakerboards) {
      this.breakerBoardNone.parentElement.remove();
      this.breakerBoard1PlankButton.parentElement.remove();
      this.breakerBoard2PlankButton.parentElement.remove();
    }
  }

  _updateUsedBoardsTotal(usedBoards, boardWidth) {
    const availableBoards = this.additionalOptions.availableBoards.map(board => board.length);
    this.usedLengthsListTotal.innerHTML = '';
    for (const availableLength of availableBoards) {
      const count = availableLength in usedBoards ? usedBoards[availableLength] : 0;
      if (count) {
        const rowItem = document.createElement('tr');

        const lengthCell = document.createElement('td');
        lengthCell.innerHTML = this.numberFormatter.formatOutput(
          cmToMm(availableLength),
          this.formatPatternBoardLengths
        );
        rowItem.appendChild(lengthCell);

        const quantityCell = document.createElement('td');
        quantityCell.innerHTML = `${count}`;
        rowItem.appendChild(quantityCell);

        const sqmCell = document.createElement('td');
        sqmCell.innerHTML = `${this.numberFormatter.formatSquareOutput(
          cm2ToMm2(count * boardWidth * availableLength),
          this.formatPatternArea
        )}`;
        rowItem.appendChild(sqmCell);

        if (this.shouldShowMetricArea()) {
          const sqmCellMetric = document.createElement('td');
          sqmCellMetric.innerHTML = `${this.numberFormatter.formatSquareOutput(
            cm2ToMm2(count * boardWidth * availableLength),
            'm2'
          )}`;
          rowItem.appendChild(sqmCellMetric);
        }

        this.usedLengthsListTotal.appendChild(rowItem);
      }
    }
  }

  _updateUsedBoardsArea(usedBoards, boardWidth) {
    const availableBoards = this.additionalOptions.availableBoards.map(board => board.length);
    this.usedLengthsListArea.innerHTML = '';
    for (const availableLength of availableBoards) {
      const count = availableLength in usedBoards ? usedBoards[availableLength] : 0;
      if (count) {
        const rowItem = document.createElement('tr');

        const lengthCell = document.createElement('td');
        lengthCell.innerHTML = `${availableLength} cm`;
        rowItem.appendChild(lengthCell);

        const quantityCell = document.createElement('td');
        quantityCell.innerHTML = `${count}`;
        rowItem.appendChild(quantityCell);

        const sqmCell = document.createElement('td');
        sqmCell.innerHTML = `${this.numberFormatter.formatSquareOutput(
          cm2ToMm2(count * boardWidth * availableLength),
          this.formatPatternArea
        )}`;
        rowItem.appendChild(sqmCell);

        if (this.shouldShowMetricArea()) {
          const sqmCellMetric = document.createElement('td');
          sqmCellMetric.innerHTML = `${this.numberFormatter.formatSquareOutput(
            cm2ToMm2(count * boardWidth * availableLength),
            'm2'
          )}`;
          rowItem.appendChild(sqmCellMetric);
        }

        this.usedLengthsListArea.appendChild(rowItem);
      }
    }
  }

  _hideUsedBoardsArea() {
    hideNode(this.usedLengthsListArea.parentElement);
  }

  _showUsedBoardsArea() {
    showNode(this.usedLengthsListArea.parentElement);
  }

  _updatePlanStats(editState, boardWidthWithoutGap) {
    this.planStats.innerHTML = '';
    // plan area
    const areaM2Item = document.createElement('li');
    areaM2Item.innerHTML = `${this.translator.translate('planSurface')}: ${this.numberFormatter.formatSquareOutput(
      editState.planSurfaceMM2,
      this.formatPatternArea
    )}`;

    this.planStats.appendChild(areaM2Item);
    // plan price
    const usedBoards = editState.usedBoardLengthCounts;
    let price = 0;
    let boardsSurface = 0;
    const tax = this.additionalOptions.showPricesTaxIncluded ? 1 + this.additionalOptions.productTax / 100 : 1;
    for (const board of this.additionalOptions.availableBoards) {
      if (board.price && board.length in usedBoards) {
        price += board.price * usedBoards[board.length] * tax;
        boardsSurface += usedBoards[board.length] * boardWidthWithoutGap * board.length;
      }
    }

    if (boardsSurface > 0) {
      const planBoardSurface = document.createElement('li');
      planBoardSurface.innerHTML = `${this.translator.translate(
        'planBoardsSurface'
      )}: ${this.numberFormatter.formatSquareOutput(cm2ToMm2(boardsSurface), this.formatPatternArea)}`;
      this.planStats.appendChild(planBoardSurface);
    }

    if (this.priceStat) {
      this._updatePriceStats(price);
    }

    if (this.wasteStat && !this.additionalOptions.tiles) {
      this._updateWasteStats(editState.totalWaste);
    }
  }

  _updatePriceStats(price) {
    this.priceStat.innerHTML = '';

    if (price > 0) {
      const currency = this.translator.translate('currency');
      const formattedPrice = this.numberFormatter.formatDecimalWithThousandsSeparator(parseFloat(price).toFixed(0));
      if (currencySymbolBefore(currency)) {
        this.priceStat.innerHTML = `${currency} ${formattedPrice}`;
      } else {
        this.priceStat.innerHTML = `${formattedPrice} ${currency}`;
      }
      if (this.priceStatHeading) {
        showNode(this.priceStatHeading);
      }
    } else {
      if (this.priceStatHeading) {
        hideNode(this.priceStatHeading);
      }
    }
  }

  _updateWasteStats(waste) {
    this.wasteStat.innerHTML = '';

    this.wasteStat.innerHTML = `${this.translator.translate(
      'configurator_total_waste'
    )}: ${this.numberFormatter.formatDecimalWithThousandsSeparator(parseFloat(waste * 100).toFixed(1))} %`;
  }

  _updateAreaStats(planarea) {
    this.areaStats.innerHTML = '';
    const areaItem = document.createElement('li');
    areaItem.innerHTML = `${this.translator.translate('areaSurface')}: ${this.numberFormatter.formatSquareOutput(
      planarea.areaState.surfaceMM2,
      this.formatPatternArea
    )}`;
    this.areaStats.appendChild(areaItem);

    if (!this.additionalOptions.tiles) {
      const wasteItem = document.createElement('li');
      wasteItem.innerHTML = `${this.translator.translate(
        'configurator_area_waste'
      )}: ${this.numberFormatter.formatDecimalWithThousandsSeparator(
        parseFloat(planarea.areaState.waste * 100).toFixed(1)
      )}%`;
      this.areaStats.appendChild(wasteItem);
    }
  }

  _handleProfiModeChange(isChecked) {
    const profiModeItems = this.sidebar.querySelectorAll('.profi-mode-tools');
    for (let item of profiModeItems) {
      if (isChecked) {
        item.style.display = 'block';
      } else {
        item.style.display = 'none';
      }
    }
    window.dispatchEvent(new Event('checkBottomBar'));
  }

  _buildSmartSelect(shouldDisableSelect) {
    const smartSelectSpan = document.createElement('span');
    smartSelectSpan.setAttribute('class', 'tp-configurator-smart-select');

    const wrapSpan = document.createElement('span');
    wrapSpan.setAttribute('class', 'tp-configurator-select-wrap');

    const select = document.createElement('select');

    wrapSpan.appendChild(select);
    smartSelectSpan.appendChild(wrapSpan);

    if (shouldDisableSelect) {
      select.disabled = true;
      wrapSpan.setAttribute('class', 'tp-configurator-select-wrap disabled');
    }

    return smartSelectSpan;
  }

  _formatTileShift(tileShift) {
    const translationKeys = [
      'konfiguration_offset_none',
      'konfiguration_offset_half',
      'konfiguration_offset_third',
      'konfiguration_offset_quarter',
      'konfiguration_offset_fifth',
    ];

    let description = '';
    if (tileShift >= 1 && tileShift <= translationKeys.length) {
      description = this.translator.translate(translationKeys[tileShift - 1]);
    }

    if (tileShift == 1) {
      return `${description}`;
    }

    let percentage = 0;
    if (tileShift != 1) {
      percentage = Math.round((1 / tileShift) * 100);
    }

    return `1/${tileShift}: ${description} (${percentage}%)`;
  }

  _renderBorderEdgeLabels(border, edgeLabelContainer) {
    if (!edgeLabelContainer) {
      return;
    }
    // First clear out any existing elements in the container
    const existingElements = edgeLabelContainer.querySelectorAll('[data-tool="edge-label-checkbox"]');
    for (let idx = 0; idx < existingElements.length; idx++) {
      edgeLabelContainer.removeChild(existingElements[idx]);
    }

    const borderLabels = border.getBorderLabels();
    const allLabels = border.getAllBorderLabels();
    for (const edgeLabel of allLabels) {
      const checkboxId = `edge-label-${edgeLabel}`;
      const checkbox = document.createElement('input');
      checkbox.setAttribute('type', 'checkbox');
      checkbox.setAttribute('id', checkboxId);
      checkbox.setAttribute('name', 'edge-label');
      checkbox.setAttribute('value', edgeLabel);

      checkbox.addEventListener('change', event => {
        if (event.target.checked) {
          border.addLabel(edgeLabel);
        } else {
          border.removeLabel(edgeLabel);
        }
        this.onTrimLabelChanged();
      });

      const label = document.createElement('label');
      label.htmlFor = checkboxId;
      label.textContent = this.translator.translate(edgeLabel);

      if (borderLabels.has(edgeLabel)) {
        checkbox.checked = true;
      }

      const wrapper = document.createElement('div');
      wrapper.setAttribute('class', 'tp-configurator-checkbox-wrap');
      wrapper.setAttribute('data-tool', 'edge-label-checkbox');
      wrapper.appendChild(checkbox);
      wrapper.appendChild(label);

      edgeLabelContainer.appendChild(wrapper);
    }
  }
}
